import React, { forwardRef, useContext, useMemo, useRef, useState } from 'react';
import PlayersLinks from './PlayersLinks';
import PlayersMarkers from './PlayersMarkers';
import { GroundFieldData, GroundFieldProps } from '../../types/webPlayer';
import { PlayersTeamsContext } from './WebPlayer';
import { useGlobalAppState } from '../../context/GlobalAppStateContext';
import VectorGround, { CenterVertices } from '../vectorGround/VectorGround';
import { coordsGPSToLPS, getSizesFromRef, isSetGlobal } from '../utils/ground';
import {
  computeScale,
  distanceGps,
  distanceLocal,
  getGroundVertices,
  getRectangleDimensions
} from '../vectorGround/lib/ground';
import {
  buildGroundContext,
  canonicalToCanvas,
  computeViewport,
  localToCanonical,
  normalizeGroundVertices,
  Viewport
} from '../vectorGround/lib';
import { computeRectangleCenter, getScale, rotateRectangle } from '../track/ground/calc';
import { processPlayerTrack, processPlayerTrackPoint } from '../vectorGround/lib/tracking';

export type VectorPlayer = {
  id: number
  name: string
  data: {
    timestamp: number
    x: number
    y: number
  }[]
};

const VectorGroundField = forwardRef<HTMLDivElement, GroundFieldProps & GroundFieldData>(( props, parentRef ) => {
  const {
    activePlayer,
    bearing,
    hoverPlayer,
    children,
    cursor,
    defaultPlayersColor,
    dataGround,
    enabledPlayers,
    infoPlayers,
    jerseyOrNumber,
    offX,
    offY,
    onPlayerClick,
    onPlayerDblClick,
    onPlayerEnter,
    onPlayerLeave,
    playersColors,
    playersDetails,
    playersLines,
    playersLinks,
    playersPoints,
    removeLink,
    scale,
    series,
    showVertices,
    teamColors,
    trailsEnabled,
    userUnits,
  } = props;

  const {playersTeams} = useContext(PlayersTeamsContext);
  const {speedMultiplier} = useGlobalAppState();

  /** NEW **/
  const boxRef = useRef<HTMLDivElement | null>(null);

  const {
    WINDOW_WIDTH,
    WINDOW_HEIGHT,
  } = getSizesFromRef(boxRef);

  const [players, setPlayers] = useState<VectorPlayer[]>([]);

  const isLPS = dataGround?.groundCoordsType === 'LOCAL';

  const groundVertices = getGroundVertices(dataGround);
  const groundVerticesArr = Object.values(groundVertices);

  const groundV = dataGround?.vertexAX || dataGround?.vertexALatitude
    ? normalizeGroundVertices(groundVerticesArr, isLPS ? 'local' : 'gps')
    : undefined;

  const ctx = groundV && buildGroundContext(
    groundV, {
      correctPerspective: groundV?.meta.maxAngleErrorDeg > 0.5,
    }
  ) || undefined;

  const PADDING = 0;

  const vp: Viewport = groundV && computeViewport(WINDOW_WIDTH, WINDOW_HEIGHT, ctx, PADDING) || undefined;

  const {scale: scaleReference} = groundV && computeScale(ctx, vp) || {scale: 1};

  const corners = {
    A: groundV && canonicalToCanvas(localToCanonical(groundV.A, ctx), vp) || {cx: 0, cy: 0},
    B: groundV && canonicalToCanvas(localToCanonical(groundV.B, ctx), vp) || {cx: 0, cy: 0},
    C: groundV && canonicalToCanvas(localToCanonical(groundV.C, ctx), vp) || {cx: 0, cy: 0},
    D: groundV && canonicalToCanvas(localToCanonical(groundV.D, ctx), vp) || {cx: 0, cy: 0},
  };

  const centerVertices: CenterVertices = {
    top: {
      x: (corners.B.cx + corners.C.cx) / 2,
      y: (corners.B.cy + corners.C.cy) / 2,
    },
    bottom: {
      x: (corners.A.cx + corners.D.cx) / 2,
      y: (corners.A.cy + corners.D.cy) / 2,
    },
  };

  const groundGeometry = useMemo(() => {
    if (
      !dataGround
    ) {
      return false;
    }

    if (isLPS) {
      const adjustedData = rotateRectangle({
        A: {
          x: dataGround.vertexAX.value,
          y: dataGround.vertexAY.value,
        },
        B: {
          x: dataGround.vertexBX.value,
          y: dataGround.vertexBY.value,
        },
        C: {
          x: dataGround.vertexCX.value,
          y: dataGround.vertexCY.value,
        },
        D: {
          x: dataGround.vertexDX.value,
          y: dataGround.vertexDY.value,
        },
      });

      // calcolo le dimensioni del campo (dai dati)
      const width = Math.abs(adjustedData.vertices.C.x - adjustedData.vertices.A.x);
      const height = Math.abs(adjustedData.vertices.C.y - adjustedData.vertices.A.y);

      // calcolo il valore della scala per adattare il campo alla canvas
      const scaleValue = getScale(
        width + 10,
        height + 10,
        WINDOW_WIDTH,
        WINDOW_HEIGHT,
      );

      return {
        vertex: {
          vertexA: {
            x: adjustedData.vertices.A.x * scaleValue,
            y: adjustedData.vertices.A.y * scaleValue,
          },
          vertexC: {
            x: adjustedData.vertices.C.x * scaleValue,
            y: adjustedData.vertices.C.y * scaleValue,
          },
          vertexB: {
            x: adjustedData.vertices.B.x * scaleValue,
            y: adjustedData.vertices.B.y * scaleValue,
          },
          vertexD: {
            x: adjustedData.vertices.D.x * scaleValue,
            y: adjustedData.vertices.D.y * scaleValue,
          },
        },
        dims: {width: width * scaleValue, height: height * scaleValue},
        center: {
          x: adjustedData.rotationInfo.centerX * scaleValue,
          y: adjustedData.rotationInfo.centerY * scaleValue,
        },
        angle: adjustedData.rotationInfo.angle * (180 / Math.PI),
        isCW: adjustedData.rotationInfo.isClockwise,
        scale: scaleValue,
      };
    }

    const {vertexA, vertexB, vertexC, vertexD, scale} = coordsGPSToLPS({
      ground: dataGround,
      WINDOW_WIDTH,
      WINDOW_HEIGHT,
    });

    const width = Math.abs(vertexC.x - vertexA.x);
    const height = Math.abs(vertexC.y - vertexA.y);

    const centro = computeRectangleCenter(vertexA, vertexC);

    return {
      vertex: {
        vertexA: {x: vertexA.x * scale, y: -vertexA.y * scale},
        vertexB: {x: vertexB.x * scale, y: -vertexB.y * scale},
        vertexC: {x: vertexC.x * scale, y: -vertexC.y * scale},
        vertexD: {x: vertexD.x * scale, y: -vertexD.y * scale},
      },
      dims: {
        width: width * scale,
        height: height * scale,
      },
      center: {
        x: centro.x * scale,
        y: -centro.y * scale,
      },
      angle: 0,
      isCW: false,
      scale,
    };
  }, [
    dataGround?.vertexAX?.value, dataGround?.vertexAY?.value,
    dataGround?.vertexBX?.value, dataGround?.vertexBY?.value,
    dataGround?.vertexCX?.value, dataGround?.vertexCY?.value,
    dataGround?.vertexDX?.value, dataGround?.vertexDY?.value,
    WINDOW_WIDTH, WINDOW_HEIGHT,
  ]);

  const rectangleDimensions = getRectangleDimensions(
    {x: corners.A.cx, y: corners.A.cy},
    {x: corners.B.cx, y: corners.B.cy},
    {x: corners.C.cx, y: corners.C.cy},
    {x: corners.D.cx, y: corners.D.cy},
  );

  const groundDetails = useMemo(() => {
    if (
      dataGround
      && dataGround.groundCoordsType === 'GLOBAL'
      && isSetGlobal(dataGround)
    ) {
      const {
        vertexA,
        vertexB,
        vertexC,
        vertexD,
      } = coordsGPSToLPS({
        ground: dataGround,
        WINDOW_WIDTH,
        WINDOW_HEIGHT,
      });

      return {
        ...dataGround,
        ...{
          vertexAX: {...dataGround.vertexAX, ...{value: vertexA.x}},
          vertexAY: {...dataGround.vertexAY, ...{value: vertexA.y}},
          vertexBX: {...dataGround.vertexBX, ...{value: vertexB.x}},
          vertexBY: {...dataGround.vertexBY, ...{value: vertexB.y}},
          vertexCX: {...dataGround.vertexCX, ...{value: vertexC.x}},
          vertexCY: {...dataGround.vertexCY, ...{value: vertexC.y}},
          vertexDX: {...dataGround.vertexDX, ...{value: vertexD.x}},
          vertexDY: {...dataGround.vertexDY, ...{value: vertexD.y}},
        },
      };
    }

    return dataGround;
  }, [dataGround, dataGround?.id, WINDOW_WIDTH, WINDOW_HEIGHT]);

  const processedPlayers = ctx && enabledPlayers?.reduce((acc, curr) => {
    if (series && series[curr]) {
      acc[curr] = processPlayerTrack(
        {
          id: curr,
          name: curr,
          data: series[curr] || {},
        },
        ctx,
        vp,
        isLPS ? 'local' : 'gps',
      );
    }

    return acc;
  }, {}) || {};

  /** /NEW **/

  const processedPlayerLinks = ctx && vp && processedPlayers && playersLinks && Array.from(playersLinks).map((pl) => {
    const player1 = processedPlayers[pl[0]]?.samples[cursor];
    const player2 = processedPlayers[pl[1]]?.samples[cursor];

    if (!player1 || !player2) {
      return null;
    }

    return {
      distance: distanceLocal({x: player1.x, y: player1.y}, {x: player2.x, y: player2.y}),
      start: [player1.cx, player1.cy],
      end: [player2.cx, player2.cy],
    };
  }) || [];

  if (!dataGround) {
    return null;
  }

  return (
    <div
      className="vector-ground-field"
      ref={boxRef}
      style={{width: '100%', height: 'auto'}}
    >
      <VectorGround
        centerVertices={centerVertices}
        corners={corners}
        vp={vp}
        groundGeometry={
          groundGeometry
            ? {
              ...groundGeometry,
              ...{
                dims: rectangleDimensions,
              },
            }
            : undefined
        }
        groundDetails={groundDetails}
        ground={{
          coordType: isLPS ? 'local' : 'gps',
          vertices: [
            {x: dataGround.vertexAX?.value || 0, y: dataGround.vertexAY?.value || 0},
            {x: dataGround.vertexBX?.value || 0, y: dataGround.vertexBY?.value || 0},
            {x: dataGround.vertexCX?.value || 0, y: dataGround.vertexCY?.value || 0},
            {x: dataGround.vertexDX?.value || 0, y: dataGround.vertexDY?.value || 0},
          ],
        }}
        players={players}
        scaleReference={scaleReference}
      >
        {
          playersLines
          && removeLink
          && (
            <PlayersLinks
              playersLines={processedPlayerLinks}
              removeLink={removeLink}
              groundSurfaceColor={dataGround?.groundSurfaceColor}
              scale={scale}
            />
          )
        }

        {
          !!(enabledPlayers
            && onPlayerClick
            && playersDetails
            && series
            && playersColors)
          && (
            <PlayersMarkers
              activePlayer={activePlayer || null}
              bearing={bearing || 0}
              hoverPlayer={hoverPlayer || null}
              cursor={cursor}
              dataGround={dataGround}
              defaultPlayersColor={defaultPlayersColor}
              enabledPlayers={enabledPlayers}
              groundSurfaceColor={dataGround?.groundSurfaceColor}
              infoPlayers={infoPlayers}
              jerseyOrNumber={!!jerseyOrNumber}
              onPlayerClick={onPlayerClick}
              onPlayerDblClick={onPlayerDblClick}
              onPlayerEnter={onPlayerEnter}
              onPlayerLeave={onPlayerLeave}
              playersColors={playersColors}
              playersDetails={playersDetails}
              playersPoints={playersPoints}
              processedPlayers={processedPlayers}
              scale={scale}
              series={series}
              speedMultiplier={speedMultiplier}
              teamColors={teamColors}
              trailsEnabled={!!trailsEnabled}
              userUnits={userUnits}
              playersTeams={playersTeams}
            />
          )
        }

        {children}
      </VectorGround>
    </div>
  );
});

export default VectorGroundField;
